// src/components/SPanel/Home/WeatherWidget.jsx
//
// „Vremea la lecție”: prognoza pentru ora lecției următoare, în sectorul ei,
// ca elevul să știe ce-l așteaptă la volan (ploaie, ceață, polei, soare).
// Fără lecție programată arată vremea de acum. Prognoza ține cel mult 16 zile.

import React, { useEffect, useState } from "react";

import { Head, Skeleton, Tile } from "./HomeTile";
import { formatCountdown, formatTime } from "./homeLessons";
import {
   CITY_COORDS,
   coordsForSector,
   drivingTips,
   forecastDaysFor,
   summarizeHours,
} from "./homeWeather";
import { getHourlyForecast } from "../../../api/weatherService";

import sunIcon from "../../../assets/svg/sun.svg";

/* ——— iconițele vremii, desenate simplu (24×24) ——— */

const CLOUD = "M7 19h10.5a4.5 4.5 0 0 0 .6-8.96A6.5 6.5 0 0 0 5.6 9.6 4.75 4.75 0 0 0 7 19z";

function WeatherIcon({ kind }) {
   const sun = (cx, cy, r) => (
      <g className="wx-sun">
         <circle cx={cx} cy={cy} r={r} />
         {Array.from({ length: 8 }, (_, i) => {
            const a = (i * Math.PI) / 4;
            return (
               <line
                  key={i}
                  x1={cx + Math.cos(a) * (r + 1.8)}
                  y1={cy + Math.sin(a) * (r + 1.8)}
                  x2={cx + Math.cos(a) * (r + 3.4)}
                  y2={cy + Math.sin(a) * (r + 3.4)}
               />
            );
         })}
      </g>
   );
   const cloud = (shift = 0) => <path className="wx-cloud" d={CLOUD} transform={`translate(0 ${shift})`} />;
   const drops = (cls) => (
      <g className={cls}>
         <line x1="8" y1="19.5" x2="7" y2="22.5" />
         <line x1="12" y1="19.5" x2="11" y2="22.5" />
         <line x1="16" y1="19.5" x2="15" y2="22.5" />
      </g>
   );

   return (
      <svg className={`wx wx--${kind}`} viewBox="0 0 24 24" aria-hidden="true">
         {kind === "clear" && sun(12, 12, 4.5)}
         {kind === "partly" && (
            <>
               {sun(8, 8, 3.2)}
               {cloud(2)}
            </>
         )}
         {kind === "cloudy" && cloud()}
         {kind === "fog" && (
            <g className="wx-fog">
               <line x1="4" y1="9" x2="20" y2="9" />
               <line x1="2" y1="13" x2="18" y2="13" />
               <line x1="5" y1="17" x2="21" y2="17" />
            </g>
         )}
         {(kind === "rain" || kind === "drizzle" || kind === "freezing") && (
            <>
               {cloud(-3)}
               {drops(kind === "freezing" ? "wx-ice" : "wx-rain")}
            </>
         )}
         {kind === "snow" && (
            <>
               {cloud(-3)}
               <g className="wx-snow">
                  <circle cx="8" cy="21" r="1.1" />
                  <circle cx="12" cy="20" r="1.1" />
                  <circle cx="16" cy="21" r="1.1" />
               </g>
            </>
         )}
         {kind === "storm" && (
            <>
               {cloud(-3)}
               <path className="wx-bolt" d="M12.5 15.5 9.5 20h2.5l-1 3.5 3.5-5h-2.6z" />
            </>
         )}
      </svg>
   );
}

/* ——— datele ——— */

function useLessonWeather(next, now) {
   const [state, setState] = useState({ status: "loading" });
   const start = next?.start || null;
   const startKey = start ? start.getTime() : "now";
   const sector = next?.sector || "";

   useEffect(() => {
      let alive = true;
      const target = start || now;
      const days = forecastDaysFor(target, new Date());
      if (start && days == null) {
         setState({ status: "far" });
         return undefined;
      }
      const [latitude, longitude] = start ? coordsForSector(sector) : CITY_COORDS;
      setState((prev) => (prev.status === "ready" ? prev : { status: "loading" }));
      let retryTimer = 0;
      const load = (attempt) =>
         getHourlyForecast({ latitude, longitude, days: Math.max(1, days || 1) })
            .then((data) => {
               if (!alive) return;
               const from = start || new Date();
               const to = next?.end || new Date(from.getTime() + 60 * 60000);
               const summary = summarizeHours(data.hourly, from, to);
               setState(summary ? { status: "ready", summary } : { status: "far" });
            })
            .catch(() => {
               if (!alive) return;
               // o cădere de rețea de moment: încă o încercare, apoi mesajul
               if (attempt === 0) retryTimer = window.setTimeout(() => load(1), 4000);
               else setState({ status: "error" });
            });
      load(0);
      return () => {
         alive = false;
         window.clearTimeout(retryTimer);
      };
      // „now” se schimbă la 30 s: prognoza se cere doar când se schimbă lecția
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [startKey, sector]);

   return state;
}

const round = (value) => (Number.isFinite(value) ? Math.round(value) : "–");

export function WeatherWidget({ size, data }) {
   const { track, now } = data;
   const next = track.next;
   const weather = useLessonWeather(next, now);
   const title = size === "1x1" ? "Vremea" : "Vremea la lecție";
   const when = next
      ? `${formatCountdown(next.start, next.end, now)}, ${formatTime(next.start)}`
      : "acum, fără lecție programată";

   if (weather.status === "loading") {
      return (
         <Tile id="weather" size={size}>
            <Head icon={sunIcon} title={title} />
            <Skeleton />
         </Tile>
      );
   }

   if (weather.status !== "ready") {
      return (
         <Tile id="weather" size={size}>
            <Head icon={sunIcon} title={title} />
            <p className="wd-weather__note">
               {weather.status === "far"
                  ? "Prognoza pentru lecția următoare apare cu vreo două săptămâni înainte."
                  : "Vremea nu s-a putut încărca acum."}
            </p>
         </Tile>
      );
   }

   const w = weather.summary;
   const tips = drivingTips(w);
   const main = (
      <div className="wd-weather__main">
         <WeatherIcon kind={w.kind} />
         <div className="wd-weather__temp">
            <strong>{round(w.temp)}°</strong>
            <span>{w.label}</span>
         </div>
      </div>
   );

   if (size === "1x1") {
      return (
         <Tile id="weather" size={size} label={`Vremea la lecție: ${round(w.temp)} grade, ${w.label}`}>
            <Head icon={sunIcon} title={title} />
            {main}
            <span className="wd-caption">{when}</span>
            <p className="wd-weather__tip wd-roomy">{tips[0]}</p>
         </Tile>
      );
   }

   return (
      <Tile id="weather" size={size}>
         <Head icon={sunIcon} title={title} extra={<span className="wd__muted">{when}</span>} />
         <div className="wd-split wd-split--weather">
            {main}
            <div className="wd-facts wd-facts--rows">
               <div className="wd-fact">
                  <span>Ploaie</span>
                  <strong>{round(w.precipProb)}%</strong>
               </div>
               <div className="wd-fact">
                  <span>Vânt</span>
                  <strong>{round(w.wind)} km/h</strong>
               </div>
               <div className="wd-fact">
                  <span>Se simte ca</span>
                  <strong>{round(w.feels)}°</strong>
               </div>
            </div>
         </div>
         <p className="wd-weather__tip">{tips[0]}</p>
      </Tile>
   );
}
